import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from './api.ts';
import { setAgencyProduct } from './site.ts';

export interface Me {
  user: { id: string; username: string; displayName: string; role: string; title: string | null; badgeNumber: string | null; totpEnabled: boolean; mustResetPassword: boolean; carIcon?: string | null };
  agency: { id: string; code: string; name: string; timezone: string; statusLabels: Record<string, string>; custodyScanRequired: boolean; autoLockMinutes: number; signatureMethods: string[]; requireFingerprintToSign: boolean; maps?: { provider: 'free' | 'esri'; esriApiKey: string | null; platformKey: boolean }; training: boolean; countyName: string | null; product?: 'bluehq' | 'civilhound'; showroom?: boolean };
  mfaVerified: boolean;
  permissions: string[];
  /** Serves civil papers (or the agency is CivilHound): the deputy menu shows the serve screens. */
  servesCivil?: boolean;
}

interface AuthState {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<Me | null>;
  logout: () => Promise<void>;
  can: (p: string) => boolean;
}

const AuthCtx = createContext<AuthState>(null as never);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    try {
      const m = await api.get<Me>('/api/auth/me');
      setMe(m);
      return m;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setMe(null);
      else if (!(e instanceof ApiError)) {
        // Offline: keep cached identity for the field app.
        const cached = sessionCache();
        setMe(cached);
        return cached;
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    refresh();
    const on = () => setMe(null);
    window.addEventListener('civilos:unauthorized', on);
    return () => window.removeEventListener('civilos:unauthorized', on);
  }, [refresh]);
  setAgencyProduct(me?.agency.product);
  useEffect(() => {
    try {
      if (me) sessionStorage.setItem('civilos.me', JSON.stringify(me));
    } catch {
      /* ignore */
    }
  }, [me]);
  const logout = async () => {
    await api.post('/api/auth/logout').catch(() => undefined);
    const { purgeOfflineData } = await import('../field/store.ts');
    await purgeOfflineData();
    try {
      sessionStorage.removeItem('civilos.me');
    } catch {
      /* ignore */
    }
    setMe(null);
  };
  const can = (p: string) => !!me?.permissions.includes(p);
  return <AuthCtx.Provider value={{ me, loading, refresh, logout, can }}>{children}</AuthCtx.Provider>;
}

function sessionCache(): Me | null {
  try {
    const s = sessionStorage.getItem('civilos.me');
    return s ? (JSON.parse(s) as Me) : null;
  } catch {
    return null;
  }
}

export const useAuth = () => useContext(AuthCtx);
